import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Popover,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import HelpTooltip from "./HelpTooltip";

export default function DateTimeRangePicker({
  prayerPlan = "fixed",
  fromDate = "",
  toDate = "",
  fromTime = "",
  toTime = "",
  numberOfDays = "",
  onFromDateChange,
  onToDateChange,
  onFromTimeChange,
  onToTimeChange,
  onNumberOfDaysChange,
  error = false,
  helperText = "",
}) {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Format 12-hour time for display
  const formatTime12 = (timeStr) => {
    if (!timeStr) return "";
    const [hh, mm] = timeStr.split(":");
    let h = parseInt(hh, 10);
    const m = mm || "00";
    if (isNaN(h)) return timeStr;
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${h}:${m} ${ampm}`;
  };

  // Format date display (e.g., Oct 15, 2026)
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "";
    const dt = new Date(dateStr);
    if (isNaN(dt.getTime())) return dateStr;
    return dt.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  // Compute calculated days
  let calculatedDays = 0;
  if (prayerPlan === "fixed" || prayerPlan === "open") {
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      if (to >= from) {
        calculatedDays = Math.ceil((to - from) / (1000 * 60 * 60 * 24)) + 1;
      }
    }
  } else if (prayerPlan === "floating") {
    calculatedDays = numberOfDays && Number(numberOfDays) > 0 ? Number(numberOfDays) : 0;
  }

  // Quick preset helper
  const handleApplyPreset = (days, startTime = "06:00", endTime = "22:00") => {
    const today = new Date();
    const fromStr = today.toISOString().split("T")[0];
    const target = new Date();
    target.setDate(today.getDate() + (days - 1));
    const toStr = target.toISOString().split("T")[0];

    if (prayerPlan === "fixed" || prayerPlan === "open") {
      onFromDateChange?.(fromStr);
      onToDateChange?.(toStr);
    } else {
      onNumberOfDaysChange?.(String(days));
    }
    onFromTimeChange?.(startTime);
    onToTimeChange?.(endTime);
  };

  // Build the display label for the single field
  let displayValue = "";
  if (prayerPlan === "fixed" || prayerPlan === "open") {
    const startStr = fromDate
      ? `${formatDateDisplay(fromDate)}${fromTime ? " at " + formatTime12(fromTime) : ""}`
      : "";
    const endStr = toDate
      ? `${formatDateDisplay(toDate)}${toTime ? " at " + formatTime12(toTime) : ""}`
      : "";

    if (startStr && endStr) {
      displayValue = `${startStr}  ➔  ${endStr}`;
    } else if (startStr) {
      displayValue = `Starts: ${startStr}`;
    }
  } else {
    // Floating
    const daysStr = numberOfDays ? `${numberOfDays} Day${Number(numberOfDays) > 1 ? "s" : ""}` : "";
    const timeStr = fromTime && toTime ? `Daily: ${formatTime12(fromTime)} ➔ ${formatTime12(toTime)}` : "";
    if (daysStr && timeStr) {
      displayValue = `${daysStr}  •  ${timeStr}`;
    } else if (daysStr || timeStr) {
      displayValue = `${daysStr} ${timeStr}`.trim();
    }
  }

  const isConfigured = Boolean(
    (prayerPlan === "floating" ? numberOfDays : fromDate && toDate) && fromTime && toTime
  );

  return (
    <Box sx={{ width: "100%" }}>
      {/* Label and Info */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.75}>
        <Box display="flex" alignItems="center" gap={0.75}>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            sx={{ color: "white", fontSize: "0.88rem" }}
          >
            Date & Time Schedule *
          </Typography>
          <HelpTooltip
            title="Date & Time Schedule"
            purpose="Sets both the start and end date and daily prayer time window together in one schedule."
            howItWorks="Click the field to set the start date & time and the concluding date & time."
            mobileEffect="Mobile users can book available prayer slots within this exact date and time window."
          />
        </Box>

        {calculatedDays > 0 && (
          <Chip
            size="small"
            label={`${calculatedDays} Day${calculatedDays > 1 ? "s" : ""} Total`}
            sx={{
              bgcolor: "rgba(76, 161, 175, 0.18)",
              color: "#64d2e2",
              border: "1px solid rgba(76, 161, 175, 0.35)",
              fontWeight: 600,
              fontSize: "0.74rem",
              height: 22,
            }}
          />
        )}
      </Box>

      {/* The Single Unified Range Field */}
      <Box
        onClick={handleClick}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 50,
          minHeight: 50,
          px: 2,
          boxSizing: "border-box",
          borderRadius: "10px",
          cursor: "pointer",
          backgroundColor: "rgba(255, 255, 255, 0.04)",
          border: error
            ? "1.5px solid #EF4444"
            : isConfigured
            ? "1.5px solid rgba(76, 161, 175, 0.6)"
            : "1px solid rgba(255, 255, 255, 0.15)",
          boxShadow: isConfigured
            ? "0 2px 10px rgba(76, 161, 175, 0.15)"
            : "none",
          transition: "all 0.22s ease",
          "&:hover": {
            borderColor: error ? "#EF4444" : "#64d2e2",
            backgroundColor: "rgba(76, 161, 175, 0.08)",
          },
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5} sx={{ minWidth: 0, flex: 1 }}>
          <CalendarMonthIcon
            sx={{
              color: isConfigured ? "#64d2e2" : "rgba(255, 255, 255, 0.5)",
              fontSize: 22,
              flexShrink: 0,
            }}
          />
          {displayValue ? (
            <Typography
              noWrap
              sx={{
                color: "#FFFFFF",
                fontWeight: 600,
                fontSize: { xs: "0.85rem", sm: "0.95rem" },
                letterSpacing: "0.2px",
              }}
            >
              {displayValue}
            </Typography>
          ) : (
            <Typography
              sx={{
                color: "rgba(255, 255, 255, 0.45)",
                fontSize: { xs: "0.85rem", sm: "0.95rem" },
              }}
            >
              Click to set Start & End Date and Time...
            </Typography>
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          {isConfigured && (
            <Tooltip title="Clear Schedule">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onFromDateChange?.("");
                  onToDateChange?.("");
                  onFromTimeChange?.("");
                  onToTimeChange?.("");
                  onNumberOfDaysChange?.("");
                }}
                sx={{
                  color: "rgba(255, 255, 255, 0.4)",
                  "&:hover": { color: "#EF4444", backgroundColor: "rgba(239, 68, 68, 0.1)" },
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}

          <Chip
            size="small"
            label={open ? "Close" : isConfigured ? "Edit" : "Set Range"}
            sx={{
              bgcolor: isConfigured ? "rgba(76, 161, 175, 0.25)" : "rgba(255, 255, 255, 0.08)",
              color: isConfigured ? "#64d2e2" : "#FFFFFF",
              border: `1px solid ${isConfigured ? "rgba(76, 161, 175, 0.5)" : "rgba(255, 255, 255, 0.15)"}`,
              fontWeight: 600,
              fontSize: "0.75rem",
              cursor: "pointer",
            }}
          />
        </Box>
      </Box>

      {/* Helper / Error Text */}
      {helperText && (
        <Typography
          variant="caption"
          sx={{ color: error ? "#EF4444" : "rgba(255, 255, 255, 0.5)", mt: 0.5, display: "block" }}
        >
          {helperText}
        </Typography>
      )}

      {/* Popover containing the 2 integrated schedule panes */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1.2,
            width: { xs: "92vw", sm: 580 },
            maxWidth: "96vw",
            p: 2.5,
            borderRadius: "16px",
            backgroundColor: "#162334",
            border: "1.5px solid rgba(76, 161, 175, 0.4)",
            boxShadow: "0 16px 40px rgba(0, 0, 0, 0.55)",
            backdropFilter: "blur(20px)",
            color: "white",
          },
        }}
      >
        {/* Popover Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" pb={1.5} borderBottom="1px solid rgba(255, 255, 255, 0.08)">
          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#FFFFFF", fontSize: "1rem" }}>
              Configure Date & Time Schedule
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.6)" }}>
              {prayerPlan === "floating"
                ? "Specify duration in days and daily start/end prayer time window"
                : "Select beginning date/time and concluding date/time for slots"}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose} sx={{ color: "rgba(255, 255, 255, 0.6)" }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Quick Presets */}
        <Box sx={{ pt: 1.5, pb: 1.5, display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
          <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.5)", mr: 0.5 }}>
            Quick Presets:
          </Typography>
          <Chip
            size="small"
            clickable
            label="Today (Full Day)"
            onClick={() => handleApplyPreset(1, "00:00", "23:59")}
            sx={{ bgcolor: "rgba(255, 255, 255, 0.06)", color: "#E0F2F1", "&:hover": { bgcolor: "rgba(76, 161, 175, 0.25)" } }}
          />
          <Chip
            size="small"
            clickable
            label="7 Days"
            onClick={() => handleApplyPreset(7, "06:00", "22:00")}
            sx={{ bgcolor: "rgba(255, 255, 255, 0.06)", color: "#E0F2F1", "&:hover": { bgcolor: "rgba(76, 161, 175, 0.25)" } }}
          />
          <Chip
            size="small"
            clickable
            label="21 Days"
            onClick={() => handleApplyPreset(21, "06:00", "22:00")}
            sx={{ bgcolor: "rgba(255, 255, 255, 0.06)", color: "#E0F2F1", "&:hover": { bgcolor: "rgba(76, 161, 175, 0.25)" } }}
          />
          <Chip
            size="small"
            clickable
            label="40 Days Chain"
            onClick={() => handleApplyPreset(40, "00:00", "23:59")}
            sx={{ bgcolor: "rgba(232, 184, 75, 0.12)", color: "#E8B84B", border: "1px solid rgba(232, 184, 75, 0.3)", "&:hover": { bgcolor: "rgba(232, 184, 75, 0.25)" } }}
          />
        </Box>

        {/* 2-Column Schedule Form */}
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", sm: "1fr auto 1fr" }}
          gap={2}
          alignItems="center"
          sx={{
            p: 2,
            borderRadius: "12px",
            backgroundColor: "rgba(255, 255, 255, 0.03)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
          }}
        >
          {/* Start Column */}
          <Box display="flex" flexDirection="column" gap={1.8}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <CalendarMonthIcon sx={{ color: "#64d2e2", fontSize: 18 }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#64d2e2" }}>
                Start Window
              </Typography>
            </Box>

            {prayerPlan === "fixed" || prayerPlan === "open" ? (
              <TextField
                label="Start Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={fromDate}
                onChange={(e) => onFromDateChange?.(e.target.value)}
              />
            ) : null}

            <TextField
              label="Start Time"
              type="time"
              size="small"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={fromTime}
              onChange={(e) => onFromTimeChange?.(e.target.value)}
            />
          </Box>

          {/* Center Divider / Arrow */}
          <Box display={{ xs: "none", sm: "flex" }} justifyContent="center">
            <ArrowForwardIcon sx={{ color: "rgba(76, 161, 175, 0.5)", fontSize: 24 }} />
          </Box>

          {/* End Column */}
          <Box display="flex" flexDirection="column" gap={1.8}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <AccessTimeIcon sx={{ color: "#64d2e2", fontSize: 18 }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#64d2e2" }}>
                End Window
              </Typography>
            </Box>

            {prayerPlan === "fixed" || prayerPlan === "open" ? (
              <TextField
                label="End Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={toDate}
                inputProps={{ min: fromDate || undefined }}
                onChange={(e) => onToDateChange?.(e.target.value)}
              />
            ) : (
              <TextField
                label="Number of Days"
                type="number"
                size="small"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={numberOfDays}
                onChange={(e) => onNumberOfDaysChange?.(e.target.value)}
              />
            )}

            <TextField
              label="End Time"
              type="time"
              size="small"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={toTime}
              inputProps={{ min: fromTime || undefined }}
              onChange={(e) => onToTimeChange?.(e.target.value)}
            />
          </Box>
        </Box>

        {/* Popover Footer */}
        <Box display="flex" alignItems="center" justifyContent="space-between" pt={2.5}>
          <Box>
            {calculatedDays > 0 && (
              <Typography variant="caption" sx={{ color: "#64d2e2", fontWeight: 600 }}>
                ✓ {calculatedDays} day{calculatedDays > 1 ? "s" : ""} scheduled
                {fromTime && toTime ? ` (${fromTime} - ${toTime})` : ""}
              </Typography>
            )}
          </Box>

          <Box display="flex" gap={1.2}>
            <Button
              size="small"
              onClick={handleClose}
              sx={{ color: "rgba(255, 255, 255, 0.6)", textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<CheckIcon />}
              onClick={handleClose}
              sx={{
                background: "linear-gradient(135deg, #177373 0%, #208e8e 100%)",
                color: "#FFFFFF",
                textTransform: "none",
                fontWeight: 600,
                px: 2,
                borderRadius: "8px",
                "&:hover": {
                  background: "linear-gradient(135deg, #1A8282 0%, #259e9e 100%)",
                },
              }}
            >
              Apply Schedule
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}
